import React, { useCallback } from 'react';
import { Banner, LegacyCard as Card, Select, TextField } from '@shopify/polaris';
import { PAYMENT_METHODS, SHIPPING_OPTIONS } from '../../../constants/storefrontOrderConstants';
import { isExpressCityMismatch } from '../../../utils/storefrontOrder/validation';

/**
 * Shipping, payment method, and optional note card. Shows a non-blocking
 * warning when express shipping is picked for a city it does not serve.
 *
 * @param {Object} props
 * @param {string} props.shippingValue - 'regular' | 'express'.
 * @param {string} props.paymentMethod - Payment method value.
 * @param {string} props.note - Internal note.
 * @param {string} props.city - Customer city (for the express warning).
 * @param {Function} props.onChange - Receives (field, value).
 * @param {boolean} [props.disabled]
 * @returns {JSX.Element}
 */
export default function ShippingPaymentCard({
  shippingValue,
  paymentMethod,
  note,
  city,
  onChange,
  disabled = false
}) {
  const handle = useCallback(
    field => value => {
      onChange(field, value);
    },
    [onChange]
  );

  const showExpressWarning = isExpressCityMismatch(shippingValue, city);

  return (
    <Card>
      <Card.Section title="Shipping & payment">
        <Select
          label="Shipping method"
          options={SHIPPING_OPTIONS.map(option => ({
            value: option.value,
            label: `${option.title} (${option.price} EGP)`
          }))}
          value={shippingValue}
          onChange={handle('shippingValue')}
          disabled={disabled}
        />
        {showExpressWarning ? (
          <Banner tone="warning">
            Express shipping is only available in Cairo and Giza.
          </Banner>
        ) : null}
        <Select
          label="Payment method"
          options={PAYMENT_METHODS.map(method => ({
            value: method.value,
            label: method.label
          }))}
          value={paymentMethod}
          onChange={handle('paymentMethod')}
          disabled={disabled}
        />
        <TextField
          label="Note (optional)"
          value={note || ''}
          onChange={handle('note')}
          multiline={3}
          disabled={disabled}
          autoComplete="off"
        />
      </Card.Section>
    </Card>
  );
}
